import {
  closestCorners,
  DndContext,
  DragEndEvent,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { Box, Card, CardContent, InputAdornment, Stack, TextField, Typography } from '@mui/material'
import { CarrierVisitDirection } from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { IRailVisitItem } from '@planning/rt-stores/railVisit/RailVisitItem'
import { useTranslate } from '@tolgee/react'
import { Loader, ResizableCard, SearchIcon, useMinimalsTheme, ZoomControl } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { FC, useCallback, useMemo, useRef } from 'react'
import { useDraggable } from 'react-use-draggable-scroll'
import { RailcarPlanningRailcarProps } from './Components/Molecules/RailcarPlanningRailcar'
import { RailcarPlanningRailtrack } from './Components/Molecules/RailcarPlanningRailtrack'
import { RailcarPlanningDragOverlay } from './Components/Organisms/RailcarPlanningDragOverlay'
import { RailcarPlanningDroppableContainer } from './Components/Organisms/RailcarPlanningDroppableContainer'
import {
  extractRailcarPlanningNumberIdentifierByType,
  RailcarPlanningDraggableEnum,
  RailcarPlanningViewStore,
} from './Stores/RailcarPlanningViewStore'

type Props = {
  railVisitItem: IRailVisitItem
  direction: CarrierVisitDirection
}

export const RailcarPlanning: FC<Props> = observer(props => {
  const theme = useMinimalsTheme()
  const { t } = useTranslate()
  const { railVisitItem, direction } = props

  const { appViewStore } = usePlanningStore()

  const railcarPlanningViewStore = useMemo(
    () => new RailcarPlanningViewStore(direction, railVisitItem),
    [direction, railVisitItem],
  )

  const checkForDifferentPods = useCallback(
    async (selectedPod: string, targetRailcar: RailcarPlanningRailcarProps) => {
      if (direction === CarrierVisitDirection.Inbound) return true

      let isValid = true
      const isDraggedToRailcarWithDifferentPods = targetRailcar.containers?.some(
        container => container.pod !== selectedPod,
      )

      if (isDraggedToRailcarWithDifferentPods) {
        isValid = await appViewStore.setOpenConfirmDialog(
          true,
          t(
            'differentPodWarningText',
            'You are about to place containers with differente PODs on the same railcar. This may lead to operational issues.',
          ),
          `${t('continueWithDifferentPods', 'Continue with different PODs')}?`,
          undefined,
          {
            maxWidth: 'sm',
            primaryActionText: t('yesContinue', 'Yes, continue'),
            secondaryActionText: t('noCancelPlacement', 'No, cancel placement'),
          },
        )
      }

      return isValid
    },
    [appViewStore, direction, t],
  )

  const checkForExceedingTeuLimit = useCallback(
    async (addedTeu: number, targetRailcar: RailcarPlanningRailcarProps) => {
      let isValid = true
      const currentTargetRailcarTeus =
        targetRailcar.containers?.reduce(
          (sum, container) => sum + (container.size ?? 20) / 20,
          0,
        ) ?? 0

      const isExceedingTeuLimit =
        targetRailcar.railcarMaxTEU &&
        currentTargetRailcarTeus + addedTeu / 20 > targetRailcar.railcarMaxTEU

      if (isExceedingTeuLimit) {
        isValid = await appViewStore.setOpenConfirmDialog(
          true,
          t(
            'exceedingTeuLimitWarningText',
            'You are about to exceed the maximum TEU capacity. This may lead to operational issues.',
          ),
          `${t('exceedMaxTeuLimit', 'Exceed maximum TEU limit')}?`,
          undefined,
          {
            maxWidth: 'sm',
            primaryActionText: t('yesExceedLimit', 'Yes, exceed limit'),
            secondaryActionText: t('noCancelPlacement', 'No, cancel placement'),
          },
        )
      }

      return isValid
    },
    [appViewStore, t],
  )

  const height = '100%'

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))
  const ref = useRef<HTMLDivElement>() as React.MutableRefObject<HTMLInputElement>
  const { events } = useDraggable(ref, {
    activeMouseButton: 'Middle',
  })

  return (
    <Box display='flex' sx={{ height }}>
      <Loader show={railcarPlanningViewStore.isLoading} />

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={(event: DragStartEvent) => railcarPlanningViewStore.onEntityDragStart(event)}
        onDragEnd={async (event: DragEndEvent) => {
          const { active } = event
          let confirmDifferentPods = true
          let confirmExceedingMaxTeuLimit = true

          const targetRailcar =
            railcarPlanningViewStore.getTargetRailcarOfContainerDroppedEvent(event)

          const isSameRailcar =
            !!active?.id &&
            targetRailcar?.containers?.some(
              c =>
                c.id ===
                extractRailcarPlanningNumberIdentifierByType(
                  active.id,
                  RailcarPlanningDraggableEnum.CONTAINER,
                ),
            )

          if (targetRailcar) {
            if (targetRailcar.isMissingRailcar || isSameRailcar) return

            confirmDifferentPods = await checkForDifferentPods(
              active.data.current?.pod,
              targetRailcar,
            )

            confirmExceedingMaxTeuLimit = await checkForExceedingTeuLimit(
              active.data.current?.size,
              targetRailcar,
            )
          } else {
            if (railcarPlanningViewStore.isSelectedContainerDeparted(active?.id)) return
          }

          if (confirmDifferentPods && confirmExceedingMaxTeuLimit) {
            await railcarPlanningViewStore.onEntityDragEnd(event)
          }
        }}
      >
        <Box
          sx={{
            height,
            display: 'flex',
            width: '100%',
            paddingRight: theme.customSpacing.m,
            paddingLeft: theme.customSpacing.m,
          }}
          gap={theme.customSpacing.m}
        >
          <ResizableCard initialWidth={450} minWidth={250} maxWidth={450}>
            <RailcarPlanningDroppableContainer
              railcarPlanningViewStore={railcarPlanningViewStore}
              containers={railcarPlanningViewStore.filteredUnassignedContainers}
            />
          </ResizableCard>
          <Box sx={{ flex: 1, minWidth: 0, height }}>
            <Card sx={{ height }}>
              <CardContent sx={{ height, position: 'relative' }}>
                <Stack
                  spacing={2}
                  sx={{ height }}
                  display='flex'
                  direction='column'
                  position='relative'
                >
                  <Stack direction='row' justifyContent='space-between' alignItems='center'>
                    <Typography variant='h4'>{t('railTracks', 'Rail tracks')}</Typography>
                    <TextField
                      size='small'
                      variant='outlined'
                      placeholder={t(
                        'searchForContainerOrRailcar',
                        'Search for container or railcar',
                      )}
                      onChange={val =>
                        railcarPlanningViewStore.setRailTrackFilter(val.target.value)
                      }
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position='start'>
                              <SearchIcon fontSize='small' />
                            </InputAdornment>
                          ),
                        },
                      }}
                      sx={{ minWidth: 350 }}
                    />
                  </Stack>

                  {/* Maybe use this to improve UX */}
                  {/* <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 1000 }}>
                        {railcarPlanningViewStore.error && (
                          <Typography
                            variant='body1'
                            color='error'
                            display='flex'
                            alignItems='center'
                            gap={0.5}
                          >
                            {t(railcarPlanningViewStore.error)}
                          </Typography>
                        )}
                        {!railcarPlanningViewStore.error && railcarPlanningViewStore.isLoading && (
                          <Typography
                            variant='body1'
                            color='success'
                            display='flex'
                            alignItems='center'
                            gap={0.5}
                          >
                            {t('saving...', 'Saving...')}
                          </Typography>
                        )}
                        {!railcarPlanningViewStore.error && !railcarPlanningViewStore.isLoading && (
                          <Typography
                            variant='body1'
                            color='textDisabled'
                            display='flex'
                            alignItems='center'
                            gap={0.5}
                          >
                            {t('saved', 'Saved')} <CheckmarkIcon sx={{ fontSize: '18px' }} />
                          </Typography>
                        )}
                      </Box> */}
                  <Stack
                    ref={ref}
                    {...events}
                    onMouseDown={event => {
                      if (event.button === 1) event.preventDefault()
                      events.onMouseDown(event)
                    }}
                    spacing={theme.customSpacing.s}
                    overflow={'auto'}
                    sx={{
                      display: 'grid',
                      height,
                      opacity: railcarPlanningViewStore.isLoading ? 0.2 : 1,
                      pointerEvents: railcarPlanningViewStore.isLoading ? 'none' : 'auto',
                      gridAutoRows: 'max-content',
                      zoom: railcarPlanningViewStore.currentZoomLevel,
                    }}
                    pb={theme.customSpacing.xll}
                  >
                    {railcarPlanningViewStore.railtracks?.map(railtrack => (
                      <RailcarPlanningRailtrack
                        key={railtrack.id}
                        railcarPlanningViewStore={railcarPlanningViewStore}
                        {...railtrack}
                        filter={railcarPlanningViewStore.railTrackFilter}
                      />
                    ))}
                  </Stack>
                </Stack>
                <Stack
                  sx={{
                    position: 'absolute',
                    bottom: '42px',
                    right: '42px',
                    zIndex: 1000,
                  }}
                >
                  <ZoomControl
                    onZoomIn={zoomLevel => railcarPlanningViewStore.setCurrentZoomLevel(zoomLevel)}
                    onZoomOut={zoomLevel => railcarPlanningViewStore.setCurrentZoomLevel(zoomLevel)}
                    onResetZoom={zoomLevel =>
                      railcarPlanningViewStore.setCurrentZoomLevel(zoomLevel)
                    }
                  />
                </Stack>
              </CardContent>
            </Card>
          </Box>
        </Box>
        <RailcarPlanningDragOverlay container={railcarPlanningViewStore.draggedContainer} />
      </DndContext>
    </Box>
  )
})
